/**
 * Set Password Page
 * 
 * Page for invited users to set their password and activate their account.
 * This is a public page accessible via invitation link.
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../../components/ui';
import { invitationService } from '../../services';
import type { TokenValidationResponse } from '../../services';

type PageState = 'loading' | 'valid' | 'invalid' | 'expired' | 'success' | 'error';

const SetPasswordPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Page state
  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenData, setTokenData] = useState<TokenValidationResponse | null>(null);
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setPageState('invalid');
        return;
      }
      
      try {
        const response = await invitationService.validateToken(token);
        
        if (response.success && response.data) {
          if (response.data.valid) {
            setTokenData(response.data);
            setPageState('valid');
          } else if (response.data.error === 'TOKEN_EXPIRED') {
            setPageState('expired');
          } else {
            setPageState('invalid');
          }
        } else {
          setPageState('invalid');
        }
      } catch {
        setPageState('error');
      }
    };
    
    validateToken();
  }, [token]);
  
  // Password validation
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return t('auth.register.errors.passwordLength');
    if (!/[A-Z]/.test(pwd)) return t('auth.register.errors.passwordUppercase');
    if (!/[a-z]/.test(pwd)) return t('auth.register.errors.passwordLowercase');
    if (!/[0-9]/.test(pwd)) return t('auth.register.errors.passwordDigit');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return t('auth.register.errors.passwordSpecial');
    return null;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setFormError(passwordError);
      return;
    }
    
    // Check passwords match
    if (password !== confirmPassword) {
      setFormError(t('auth.register.errors.passwordMismatch'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await invitationService.setPassword({
        token: token!,
        password,
      });
      
      if (response.success && response.data?.success) {
        setPageState('success');
      } else {
        setFormError(response.error?.message || t('invitation.setPassword.errors.generic'));
      }
    } catch {
      setFormError(t('invitation.setPassword.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render loading state
  if (pageState === 'loading') {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">{t('invitation.setPassword.validating')}</p>
        </div>
      </Card>
    );
  }
  
  // Render invalid token state
  if (pageState === 'invalid') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('invitation.setPassword.invalid.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('invitation.setPassword.invalid.message')}
          </p>
          <Link to="/auth/login">
            <Button variant="secondary">{t('invitation.setPassword.backToLogin')}</Button>
          </Link>
        </div>
      </Card>
    );
  }
  
  // Render expired token state
  if (pageState === 'expired') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('invitation.setPassword.expired.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('invitation.setPassword.expired.message')}
          </p>
          <Link to="/auth/login">
            <Button variant="secondary">{t('invitation.setPassword.backToLogin')}</Button>
          </Link>
        </div>
      </Card>
    );
  }
  
  // Render error state
  if (pageState === 'error') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('invitation.setPassword.error.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('invitation.setPassword.error.message')}
          </p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </Button>
        </div>
      </Card>
    );
  }
  
  // Render success state
  if (pageState === 'success') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('invitation.setPassword.success.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('invitation.setPassword.success.message')}
          </p>
          <Button onClick={() => navigate('/auth/login')} className="w-full">
            {t('invitation.setPassword.success.login')}
          </Button>
        </div>
      </Card>
    );
  }
  
  // Render form (valid token)
  return (
    <Card>
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-violet-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t('invitation.setPassword.title')}
        </h1>
        <p className="text-slate-500 mt-2">
          {t('invitation.setPassword.subtitle', {
            name: `${tokenData?.firstName} ${tokenData?.lastName}`,
          })}
        </p>
      </div>
      
      {/* User info */}
      <div className="bg-slate-50 rounded-lg p-4 mb-6">
        <div className="text-sm text-slate-600">
          <p>
            <span className="font-medium">{t('invitation.setPassword.invitedBy')}:</span>{' '}
            {tokenData?.ownerEmail}
          </p>
          <p>
            <span className="font-medium">{t('auth.email')}:</span>{' '}
            {tokenData?.email}
          </p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          helperText={t('auth.register.passwordHint')}
          disabled={isSubmitting}
        />
        
        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          disabled={isSubmitting}
        />
        
        {formError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {formError}
          </div>
        )}
        
        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          {t('invitation.setPassword.submit')}
        </Button>
      </form>
      
      <div className="mt-6 text-center text-sm text-slate-500">
        {t('invitation.setPassword.alreadyHaveAccount')}{' '}
        <Link to="/auth/login" className="text-violet-600 hover:text-violet-700 font-medium">
          {t('auth.register.login')}
        </Link>
      </div>
    </Card>
  );
};

export default SetPasswordPage;
